import { useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { PersonEvent } from "@typedoc/interview/events";
import type { Session } from "@typedoc/interview/session";
import { z } from "zod";
import { Island, IslandUnavailable } from "../../components/recordings/island";
import { regionalOptions } from "../../lib/access-client";
import { AppError } from "../../lib/app-error";
import { chatApi } from "../../lib/chats/client";
import { useCurrentTime } from "../../lib/chats/use-current-time";
import { useIslandCapture } from "../../lib/recordings/capture";
import {
  desktopCapture,
  desktopCloseIsland,
  desktopIslandVoice,
  desktopKeepsIsland,
  useIslandRecording,
  useIslandShape,
} from "../../lib/recordings/desktop";
import { announceDiscarded } from "../../lib/recordings/discarded-signal";
import { createLiveRecordings } from "../../lib/recordings/live-port";
import {
  createSampleDesktop,
  isShareScene,
  SHARE_SCENES,
} from "../../lib/recordings/sample-desktop";
import { createSampleIslandPort, SAMPLE_SCENES } from "../../lib/recordings/sample-port";
import { islandStore, useIsland, type IslandStore } from "../../lib/recordings/use-island";
import { useIslandVoice, type ConnectVoiceRoom } from "../../lib/recordings/voice";
import { useWorkspaceScope } from "../../lib/workspace-scope";

/**
 * The island (ENG-1016): the recording's interview, drawn on its own for the desktop app's
 * floating window, which the shell opens on this route. It follows the same build-time switch as Chats: the sample session in this
 * page's memory (`scene` opens it on a state worth looking at), or the Recordings API for the
 * recording named in the address. The sample's share scenes (ENG-1031) add a desktop shell in the
 * page's memory, so the choice of what Abacor sees can be looked at in a browser.
 */
const islandSearchSchema = z.object({
  recording: z.string().optional().catch(undefined),
  scene: z
    .enum([...SAMPLE_SCENES, ...SHARE_SCENES])
    .optional()
    .catch(undefined),
});

/**
 * Cancel before the recording starts: it ends, is taken away, and the island closes, with no end
 * and no countdown (`gone`: the island is drawn no more once the end has landed). When the end did
 * not reach the recording, the island stays, with its Try again, and remembers it was a cancel:
 * once the end lands, sent again from Try again, the recording is taken away and the island closes
 * all the same, rather than the recording staying in the chat.
 */
function useCancelStart(
  recordingId: string | null | undefined,
  send: (event: PersonEvent) => Promise<boolean>,
  store: IslandStore,
  mode: Session["mode"] | undefined,
) {
  const [cancelled, setCancelled] = useState(false);
  const done = useRef(false);
  const gone = cancelled && mode === "finished";
  const finish = useEffectEvent(async () => {
    if (done.current) return;
    done.current = true;
    const discarded = await store.discard().catch(() => false);
    // The chat that started it, in its own window, lets its card go too.
    if (discarded && recordingId) announceDiscarded(recordingId);
    desktopCloseIsland()?.();
  });
  useEffect(() => {
    if (gone) void finish();
  }, [gone]);
  const cancel = async () => {
    setCancelled(true);
    await send({ type: "person.finish" });
  };
  return { cancel, gone };
}

export const Route = createFileRoute("/_authenticated/island")({
  validateSearch: islandSearchSchema,
  component: IslandRoute,
});

/** LiveKit's client, loaded only when an island joins a voice room. */
const connectLiveKit: ConnectVoiceRoom = async (join, handlers, signal) =>
  (await import("../../lib/recordings/livekit-room")).connectLiveKit(join, handlers, signal);

/** The island redraws this often, for its clock and for receipts and lines that run out. */
const TICK_MS = 500;

function IslandRoute() {
  const { recording, scene = "interview" } = Route.useSearch();
  // In the desktop app's window, only the island takes clicks; the rest of the window passes them.
  const page = useRef<HTMLElement>(null);
  useIslandShape(page, recording);
  // A desktop app that keeps the island between recordings (bridge version 10) readies this page
  // hidden and says which recording to show, in place: a new recording draws without loading the
  // web app again, and null puts the island back to waiting.
  const navigate = Route.useNavigate();
  useIslandRecording(
    (next) =>
      void navigate({
        search: (search) => ({ ...search, recording: next ?? undefined }),
        replace: true,
      }),
  );
  const waiting = !recording && desktopKeepsIsland();
  // The page draws nothing behind the island (`island.css`). A class on the root, not a
  // `:root:has(.island-page)` rule: Chrome checks that against the whole document on every change
  // to any page, which held a long recording's timeline up while it scrolled (Nov 2, 2026).
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("island-root");
    return () => root.classList.remove("island-root");
  }, []);
  return (
    <main id="main" className="island-page" ref={page}>
      <h1 className="sr-only">Recording</h1>
      {waiting ? (
        <WaitingIsland />
      ) : chatApi === "sample" ? (
        <SampleIsland key={`${scene}:${recording ?? ""}`} scene={scene} recording={recording} />
      ) : (
        // Each recording is an island of its own: nothing of the last carries into the next.
        <LiveIsland key={recording} recording={recording ?? null} />
      )}
    </main>
  );
}

function SampleIsland({
  scene,
  recording,
}: {
  scene: (typeof SAMPLE_SCENES)[number] | (typeof SHARE_SCENES)[number];
  recording: string | undefined;
}) {
  const store = useMemo(
    () =>
      islandStore(() =>
        createSampleIslandPort({
          scene: isShareScene(scene) ? "interview" : scene,
          ...(recording ? { recordingId: recording } : {}),
        }),
      ),
    [scene, recording],
  );
  // The share scenes stand in for a desktop app that lets the person choose what Abacor sees.
  const desktop = useMemo(
    () => (isShareScene(scene) ? createSampleDesktop(scene) : globalThis.window?.desktop),
    [scene],
  );
  const island = useIsland(store);
  const { send, sharing } = useIslandCapture(
    island.state.session?.mode,
    (island.state.session?.startedAt ?? null) !== null,
    island.state.session?.personSpeaking ?? false,
    island.send,
    store.grant,
    desktop,
  );
  const now = useCurrentTime(TICK_MS).getTime();
  const cancelStart = useCancelStart(recording, send, store, island.state.session?.mode);
  if (cancelStart.gone) return null;
  return (
    <Island
      state={island.state}
      now={now}
      send={send}
      retry={island.retry}
      dismiss={island.dismiss}
      sharing={sharing}
      cancelStart={cancelStart.cancel}
    />
  );
}

function LiveIsland({ recording }: { recording: string | null }) {
  const scope = useWorkspaceScope();
  const { userId, workspaceId, ready } = scope;
  const recordings = useMemo(
    () =>
      ready
        ? createLiveRecordings({
            userId,
            workspaceId,
            options: regionalOptions,
          })
        : null,
    [ready, userId, workspaceId],
  );
  const store = useMemo(
    () =>
      islandStore(() =>
        recordings && recording ? recordings.open(recording) : missing(recordings !== null),
      ),
    [recordings, recording],
  );
  const island = useIsland(store);
  // In a desktop app that captures, the recording's screens: only for a recording this port has.
  const { send, sharing } = useIslandCapture(
    island.state.session?.mode,
    (island.state.session?.startedAt ?? null) !== null,
    island.state.session?.personSpeaking ?? false,
    island.send,
    recordings && recording ? store.grant : null,
  );
  // Abacor's voice, for a recording that has it, where the island can use the mic (ENG-1017).
  // Where the person first chooses what Abacor sees, the island enters the room once they press
  // Start: the opening is asked from there, and nobody is talked to while they choose. The worker
  // is sent at once, so it is ready by then.
  const choosesFirst = useMemo(() => desktopCapture()?.sharing !== undefined, []);
  const voice = useIslandVoice(
    island.state.session,
    recordings && recording ? store.voiceJoin : null,
    desktopIslandVoice() ? connectLiveKit : null,
    send,
    !choosesFirst || (island.state.session?.startedAt ?? null) !== null,
  );
  const now = useCurrentTime(TICK_MS).getTime();
  const cancelStart = useCancelStart(recording, send, store, island.state.session?.mode);
  // Finding the workspace can fail, or find none; neither is loading, so neither waits.
  if (scope.failure) {
    return (
      <IslandUnavailable
        message={scope.failure.message}
        retry={async () => {
          scope.retry();
        }}
      />
    );
  }
  if (scope.noWorkspace) {
    return <IslandUnavailable message="This session is not in a workspace." />;
  }
  if (cancelStart.gone) return null;
  return (
    <Island
      state={island.state}
      now={now}
      send={send}
      retry={island.retry}
      dismiss={island.dismiss}
      sharing={sharing}
      cancelStart={cancelStart.cancel}
      voice={voice}
    />
  );
}

/**
 * The island waiting, hidden, for the desktop app's next recording: nothing drawn, but the
 * workspace already found, so the recording's island draws as soon as its tape is read.
 */
function WaitingIsland() {
  useWorkspaceScope();
  return null;
}

/** A port for no recording: it waits while the workspace is found, and refuses once it is. */
function missing(ready: boolean) {
  return {
    read: () => (ready ? Promise.reject(new AppError("NOT_FOUND")) : new Promise<never>(() => {})),
    send: () => Promise.reject(new AppError("NOT_FOUND")),
    watch: () => () => {},
  };
}
